import { useEffect, useState } from 'react'
import { Dialog } from 'radix-ui'
import { useCreateSection, useUpdateSection } from '../hooks'
import type { FormSection } from '../types'

const CODE_REGEX = /^[a-z][a-z0-9_]{0,63}$/

type Mode = { kind: 'create' } | { kind: 'edit'; section: FormSection }

export function useSectionFormSheet(objectType: string) {
  const [mode, setMode] = useState<Mode | null>(null)
  return {
    openCreate: () => setMode({ kind: 'create' }),
    openEdit: (section: FormSection) => setMode({ kind: 'edit', section }),
    sheet: mode && (
      <SectionFormSheet
        objectType={objectType}
        mode={mode}
        onClose={() => setMode(null)}
      />
    ),
  }
}

function SectionFormSheet({ objectType, mode, onClose }: {
  objectType: string
  mode: Mode
  onClose: () => void
}) {
  const create = useCreateSection(objectType)
  const update = useUpdateSection(objectType)

  const [code, setCode] = useState(mode.kind === 'edit' ? mode.section.code : '')
  const [labelPl, setLabelPl] = useState(mode.kind === 'edit' ? mode.section.labelPl : '')
  const [sortOrder, setSortOrder] = useState(mode.kind === 'edit' ? mode.section.sortOrder : 100)
  const [isActive, setIsActive] = useState(mode.kind === 'edit' ? mode.section.isActive : true)
  const [renderTarget, setRenderTarget] = useState<'TAB' | 'SIDEBAR'>(
    mode.kind === 'edit' ? mode.section.renderTarget : 'TAB',
  )
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (mode.kind === 'edit') {
      setCode(mode.section.code)
      setLabelPl(mode.section.labelPl)
      setSortOrder(mode.section.sortOrder)
      setIsActive(mode.section.isActive)
      setRenderTarget(mode.section.renderTarget)
    } else {
      setCode('')
      setLabelPl('')
      setSortOrder(100)
      setIsActive(true)
      setRenderTarget('TAB')
    }
    setError(null)
  }, [mode])

  const submit = () => {
    setError(null)
    if (mode.kind === 'create') {
      if (!CODE_REGEX.test(code)) {
        setError('Kod: małe litery, cyfry, podkreślenia. Musi zaczynać się od litery.')
        return
      }
      if (!labelPl.trim()) { setError('Etykieta nie może być pusta.'); return }
      create.mutate(
        { code, labelPl, sortOrder, isActive, renderTarget },
        { onSuccess: onClose, onError: e => setError(formatError(e)) },
      )
    } else {
      if (!labelPl.trim()) { setError('Etykieta nie może być pusta.'); return }
      update.mutate(
        { code: mode.section.code, payload: { labelPl, sortOrder, isActive, renderTarget } },
        { onSuccess: onClose, onError: e => setError(formatError(e)) },
      )
    }
  }

  const isSubmitting = create.isPending || update.isPending

  return (
    <Dialog.Root open onOpenChange={o => { if (!o) onClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed right-0 top-0 z-50 h-full w-[420px] bg-white shadow-xl p-5 overflow-auto">
          <Dialog.Title className="text-base font-semibold">
            {mode.kind === 'create' ? 'Nowa sekcja' : `Edycja sekcji ${mode.section.code}`}
          </Dialog.Title>

          <div className="mt-4 flex flex-col gap-3 text-sm">
            <label className="grid gap-1">
              <span className="text-xs uppercase text-slate-500">Kod (klucz techn.)</span>
              <input
                value={code}
                onChange={e => setCode(e.target.value)}
                disabled={mode.kind === 'edit' || isSubmitting}
                className="border rounded px-2 py-1 font-mono"
                placeholder="ogolne"
              />
              <span className="text-xs text-slate-400">snake_case, niezmienialny po utworzeniu</span>
            </label>

            <label className="grid gap-1">
              <span className="text-xs uppercase text-slate-500">Etykieta</span>
              <input
                value={labelPl}
                onChange={e => setLabelPl(e.target.value)}
                disabled={isSubmitting}
                className="border rounded px-2 py-1"
                placeholder="Ogólne"
              />
            </label>

            <div className="grid gap-1">
              <span className="text-xs uppercase text-slate-500">Miejsce renderowania</span>
              <div className="flex flex-col gap-1">
                {([
                  { value: 'TAB', label: 'Zwykła sekcja (zakładka)', desc: 'Wyświetlana w zakładkach głównego formularza' },
                  { value: 'SIDEBAR', label: 'Sidebar umowy', desc: 'Wyświetlana w bocznym panelu widoku umowy' },
                ] as const).map(opt => (
                  <label
                    key={opt.value}
                    className="flex items-start gap-2 rounded border border-slate-200 px-3 py-2 cursor-pointer hover:bg-slate-50"
                  >
                    <input
                      type="radio"
                      name="renderTarget"
                      value={opt.value}
                      checked={renderTarget === opt.value}
                      onChange={() => setRenderTarget(opt.value)}
                      disabled={isSubmitting}
                      className="mt-0.5 size-4 accent-sky-600 shrink-0"
                    />
                    <div>
                      <div className="text-sm font-medium text-slate-900">{opt.label}</div>
                      <div className="text-xs text-slate-400">{opt.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={isActive}
                onChange={e => setIsActive(e.target.checked)}
                disabled={isSubmitting}
              />
              <span>Aktywna</span>
            </label>

            {error && <p className="text-red-600 text-xs">{error}</p>}

            <div className="flex justify-end gap-2 mt-2">
              <button type="button" onClick={onClose} className="px-3 py-1.5 border rounded" disabled={isSubmitting}>
                Anuluj
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={isSubmitting}
                className="px-3 py-1.5 bg-sky-600 text-white rounded disabled:opacity-50"
              >
                {isSubmitting ? 'Zapisywanie...' : 'Zapisz'}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function formatError(err: unknown): string {
  const ax = err as { response?: { data?: { detail?: string; title?: string } } }
  return ax.response?.data?.detail ?? ax.response?.data?.title ?? 'Błąd zapisu'
}
